import {
	createContext,
	FC,
	Fragment,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { Alert, Divider, Form, Radio, Result, Table, Tabs } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from '@/libs/antd/form';
import FleetTitle from '@/libs/antd/components/Title';
import {
	createCustomerV2,
	CustomerGroup,
	CustomerPayload,
	getCustomerDetail,
	getCustomerDocumentList,
	updateCustomerV2,
} from '@/libs/fleet-api/services/customer';
import {
	AddressMasterData,
	CountryMasterData,
	IndustryMasterData,
	MasterData,
} from '@/libs/fleet-api/services/master-data';
import { FileData } from '@/libs/fleet-api/services/types';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FRMCustomerData from './CustomerPage.Data';
import FRMCustomerLegal from './CustomerPage.Legal';
import FRMCustomerReferral, {
	FRMCustomerReferralForm,
	useFRMCustomerReferralForm,
} from './CustomerPage.Referral';
import FRMCustomerBusiness, {
	FRMCustomerBusinessForm,
	useFRMCustomerBusinessForm,
} from './CustomerPage.Business';
import {
	FRMCustomerLegalDocumentForm,
	useFRMCustomerLegalDocumentForm,
} from './CustomerPage.Legal.Document';
import {
	FRMCustomerLegalManagerForm,
	useFRMCustomerLegalManagerForm,
} from './CustomerPage.Legal.Manager';
import {
	FRMCustomerLegalShareholderForm,
	useFRMCustomerLegalShareholderForm,
} from './CustomerPage.Legal.Shareholder';
import { useModals } from '@/components/modals/ModalProvider';
import FleetModal from '@/libs/antd/components/Modal';
import TextArea from 'antd/es/input/TextArea';
import fetcher from '@/libs/fleet-api/fetcher';
import { useApp } from '@/App';
import dayjs from 'dayjs';
import { NamePath } from 'antd/es/form/interface';
import GenericErrorMessage from '@/components/GenericErrorMessage';

export type CreateCustomerForm = {
	customerInformation: CustomerInformationForm;
	customerLegalDocuments: CustomerLegalDocumentForm[];
	customerBusinessDocuments: CustomerBusinessDocumentForm[];
	customerReferralDocuments: CustomerReferralDocumentsForm[];
	customerCompanyManagementInformation: CustomerCompanyManagementInformationForm[];
	customerCompanyShareholderInformation: CustomerCompanyShareholderInformationForm[];
	notes: string;
};

export type CustomerInformationForm = {
	customerType: number;
	businessEntity: MasterData;
	companyType: MasterData;
	customerName: string;
	npwpNo: string;
	companyEmail: string;
	companyAddress: string;
	companyGroup: CustomerGroup;
	rt: string;
	rw: string;
	address: AddressMasterData;
	industrySector: IndustryMasterData;
	picName: string;
	picPosition: MasterData;
	additionalInformation: string;
	isDraft: boolean;
	deviation: {
		background: string;
		mitigation: string;
		risk: string;
	};
};

export type CustomerBusinessDocumentForm = {
	id: number;
	file: FileData;
	documentNo: string;
	documentDate: string;
	documentType: string;
	defaultDocumentNo: string;
	isDocumentDateMandatory: boolean;
	isDocumentNoMandatory: boolean;
	publisher: MasterData;
	publishers: MasterData[];
	validityPeriod: MasterData;
	validityPeriods: MasterData[];
	isMandatory: boolean;
	isMandatory2: boolean;
};

export type CustomerReferralDocumentsForm = {
	id?: number;
	file: FileData;
	documentNo: string;
	documentDate: string;
	documentType: string;
	defaultDocumentNo: string;
	isDocumentDateMandatory: boolean;
	isDocumentNoMandatory: boolean;
	isMandatory: boolean;
	isMandatory2: boolean;
};

export type CustomerLegalDocumentForm = {
	id: number;
	file: FileData;
	documentNo: string;
	documentDate: string;
	documentType: string;
	defaultDocumentNo: string;
	isDocumentDateMandatory: boolean;
	isDocumentNoMandatory: boolean;
	isMandatory: boolean;
	isMandatory2: boolean;
};

export type CustomerCompanyManagementInformationForm = {
	id?: number;
	tempId?: number;
	customerId?: string;
	name: string;
	citizenship: MasterData;
	nationality: CountryMasterData;
	identityCardType: MasterData;
	identityNumber: string;
	dateOfBirth: string;
	position: MasterData;
	positionId?: MasterData;
	address: string;
	rt: string;
	rw: string;
	postal: AddressMasterData;
	phoneNumber: string;
	status: MasterData;
	identityFile: FileData;
	isShareholder?: boolean;
	shareholding?: number;
	shareholderId?: number;
};

export type CustomerCompanyShareholderInformationForm = {
	id?: number;
	tempId?: number;
	customerId?: string;
	name: string;
	citizenship: MasterData;
	nationality: CountryMasterData;
	identityCardType: MasterData;
	identityNumber: string;
	address: string;
	rt: string;
	rw: string;
	postal: AddressMasterData;
	phoneNumber: string;
	status: MasterData;
	identityFile: FileData;
	isManagement: boolean;
	shareholding: number;
	shareholderType: MasterData;
};

export const useFRMCustomerPage = () => {
	return useContext(context)!;
};

export const useFRMCustomerFormAlert = (
	fieldName: NamePath<CreateCustomerForm>,
) => {
	const { customerForm } = useFRMCustomerPage();
	const { form, value } = customerForm;

	const [alerts, setAlerts] = useState<string[]>([]);

	useEffect(() => {
		if (form.isFieldTouched(fieldName)) {
			form
				.validateFields([fieldName], {
					validateOnly: true,
					recursive: true,
				})
				.then(() => {
					setAlerts([]);
				})
				.catch((err) => {
					const messages =
						err?.errorFields
							?.map?.((err: object) => 'errors' in err && err?.errors)
							?.flat?.() ?? [];
					setAlerts(messages);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value, form]);

	return alerts;
};

const useFRMCustomerForm = () => {
	return useForm<CreateCustomerForm>();
};

export type ValidationStatus = Record<
	'legal' | 'business' | 'referral' | 'management' | 'shareholder' | 'customer',
	boolean
>;

type PageContext = null | {
	customerId?: string;
	isEdit: boolean;
	externalTrigger?: boolean;
	isSubmitting: boolean;
	totalShareholding: number;
	validationStatus: ValidationStatus;
	customerDetail?: Awaited<ReturnType<typeof getCustomerDetail>>['data'];
	customerDocument?: Awaited<
		ReturnType<typeof getCustomerDocumentList>
	>['data'];
	customerForm: ReturnType<typeof useFRMCustomerForm>;
	businessForm: ReturnType<typeof useFRMCustomerBusinessForm>;
	legalDocumentForm: ReturnType<typeof useFRMCustomerLegalDocumentForm>;
	managerForm: ReturnType<typeof useFRMCustomerLegalManagerForm>;
	shareholderForm: ReturnType<typeof useFRMCustomerLegalShareholderForm>;
	referrallForm: ReturnType<typeof useFRMCustomerReferralForm>;
};

const context = createContext<PageContext>(null);
const { Provider } = context;

export const FRMCustomerPage: FC<{
	customerId: string;
	externalTrigger?: boolean;
}> = ({ customerId, externalTrigger }) => {
	const { notification, navigate } = useApp();
	const { modal } = useModals();
	const isEdit = Boolean(customerId);
	const [activeTabKey, setActiveTabKey] = useState<string>('customer');
	const [showNotes, setShowNotes] = useState(false);

	const customerDetailQuery = useQuery({
		queryKey: ['getCustomerDetail', customerId],
		queryFn: () => {
			if (!customerId) return;
			return getCustomerDetail(customerId);
		},
		enabled: isEdit,
	});

	useEffect(() => {
		if (customerId && isEdit) {
			customerDetailQuery.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerId]);

	const customerForm = useFRMCustomerForm();
	const businessForm = useFRMCustomerBusinessForm();
	const legalDocumentForm = useFRMCustomerLegalDocumentForm();
	const managerForm = useFRMCustomerLegalManagerForm();
	const shareholderForm = useFRMCustomerLegalShareholderForm();
	const referrallForm = useFRMCustomerReferralForm();

	const { form, value, fieldName } = customerForm;
	const sectorId = value?.customerInformation?.industrySector?.id;
	const businessEntityId = value?.customerInformation?.businessEntity?.id;
	const customerTypeId = value?.customerInformation?.customerType;

	const totalShareholding = useMemo(() => {
		return Number(
			(
				customerForm.value?.customerCompanyShareholderInformation?.reduce(
					(acc, shareholder) => acc + shareholder.shareholding,
					0,
				) ?? 0
			).toFixed(2),
		);
	}, [customerForm.value]);

	const customerDocumentQuery = useQuery({
		queryKey: [
			'getCustomerDocumentList',
			sectorId,
			businessEntityId,
			customerTypeId,
		] as const,
		queryFn: () => {
			if (!sectorId || !businessEntityId || !customerTypeId) return null;
			return getCustomerDocumentList({
				sectorId,
				businessEntityId,
				customerTypeId,
			});
		},
		enabled: false,
	});

	const validationStatus = useMemo(() => {
		const checkDocumentValid = (
			documents:
				| CustomerLegalDocumentForm[]
				| CustomerBusinessDocumentForm[]
				| CustomerReferralDocumentsForm[] = [],
			isOptional = false,
		) => {
			return (
				documents.every((record) => {
					return record.isMandatory
						? record.isMandatory && !!record.file?.id
						: true;
				}) ?? isOptional
			);
		};

		const {
			customerLegalDocuments,
			customerBusinessDocuments,
			customerReferralDocuments,
			customerCompanyManagementInformation,
			customerCompanyShareholderInformation,
			customerInformation,
		} = customerForm.value ?? {};

		const status: ValidationStatus = {
			legal: checkDocumentValid(customerLegalDocuments, true),
			business: checkDocumentValid(customerBusinessDocuments, true),
			referral: checkDocumentValid(customerReferralDocuments, true),
			management: Boolean(customerCompanyManagementInformation?.length),
			shareholder: customerCompanyShareholderInformation?.length
				? totalShareholding === 100
				: true,
			customer: Boolean(
				customerInformation?.npwpNo &&
				customerInformation?.businessEntity &&
				customerInformation?.customerName &&
				customerInformation?.companyEmail &&
				customerInformation?.companyAddress &&
				customerInformation?.rt &&
				customerInformation?.rw &&
				customerInformation?.address &&
				customerInformation?.industrySector &&
				customerInformation?.picName &&
				customerInformation?.picPosition &&
				customerInformation?.additionalInformation,
			),
		};

		return status;
	}, [customerForm.value, totalShareholding]);

	useEffect(() => {
		if (!sectorId || !businessEntityId || !customerTypeId) return;
		customerDocumentQuery.refetch().then((res) => {
			const {
				customerLegalDocuments,
				customerBusinessDocuments,
				customerReferralDocuments,
			} = res.data?.data ?? {};

			customerForm.form.setFieldValue(
				customerForm.fieldName('customerLegalDocuments'),
				customerLegalDocuments?.map((doc) => {
					const userDocument = customerDetail?.customerLegalDocuments?.find(
						({ id }) => id === doc.id,
					);

					return {
						id: doc.id,
						file: userDocument?.file as FileData | undefined,
						documentNo: userDocument?.documentNo || doc.defaultDocumentNo,
						documentDate: userDocument?.documentDate,
						documentType: doc.documentType,
						defaultDocumentNo: doc.defaultDocumentNo,
						isDocumentNoMandatory: doc.isDocumentNoMandatory,
						isDocumentDateMandatory: doc.isDocumentDateMandatory,
						isMandatory: doc.isMandatory,
						isMandatory2: doc.isMandatory2,
					} as CustomerLegalDocumentForm;
				}) ?? [],
			);

			customerForm.form.setFieldValue(
				customerForm.fieldName('customerBusinessDocuments'),
				customerBusinessDocuments?.map((doc) => {
					const userDocument = customerDetail?.customerBusinessDocuments?.find(
						({ id }) => id === doc.id,
					);

					return {
						id: doc.id,
						file: userDocument?.file as FileData | undefined,
						documentNo: userDocument?.documentNo,
						documentDate: userDocument?.documentDate,
						documentType: doc.documentType,
						defaultDocumentNo: doc.defaultDocumentNo,
						isDocumentDateMandatory: doc.isDocumentDateMandatory,
						isDocumentNoMandatory: doc.isDocumentNoMandatory,
						publisher: userDocument?.publisher,
						publishers: doc.publishers,
						validityPeriod: userDocument?.validityPeriod,
						validityPeriods: doc.validityPeriods,
						isMandatory: doc.isMandatory,
						isMandatory2: doc.isMandatory2,
					} as CustomerBusinessDocumentForm;
				}) ?? [],
			);

			customerForm.form.setFieldValue(
				customerForm.fieldName('customerReferralDocuments'),
				customerReferralDocuments?.map((doc) => {
					const userDocument = customerDetail?.customerReferralDocuments?.find(
						({ id }) => id === doc.id,
					);

					return {
						id: doc.id,
						file: userDocument?.file as FileData | undefined,
						documentNo: userDocument?.documentNo,
						documentDate: userDocument?.documentDate,
						documentType: doc.documentType,
						defaultDocumentNo: doc.defaultDocumentNo,
						isDocumentDateMandatory: doc.isDocumentDateMandatory,
						isDocumentNoMandatory: doc.isDocumentNoMandatory,
						isMandatory: doc.isMandatory,
						isMandatory2: doc.isMandatory2,
					} as CustomerReferralDocumentsForm;
				}) ?? [],
			);
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sectorId, businessEntityId, customerTypeId]);

	const createCustomerV2Mutation = useMutation({
		mutationFn: createCustomerV2,
	});

	const updateCustomerV2Mutation = useMutation({
		mutationFn: updateCustomerV2,
	});

	const handleSubmit = async (values: CreateCustomerForm) => {
		const {
			customerInformation,
			customerLegalDocuments,
			customerReferralDocuments,
			customerBusinessDocuments,
			customerCompanyManagementInformation,
			customerCompanyShareholderInformation,
			notes,
		} = values;

		const isESTAR = customerDetail?.customerInformation.source === 'Estar';
		const payload: CustomerPayload = {
			customerId: customerDetail?.customerInformation?.id,
			notes,
			source: isESTAR ? 'Estar' : undefined,
			customerInformation: {
				customerType: customerInformation.customerType,
				businessEntity: customerInformation.businessEntity?.id,
				companyType: customerInformation.companyType?.id,
				customerName: customerInformation.customerName?.toUpperCase(),
				companyEmail: customerInformation.companyEmail,
				companyAddress: customerInformation.companyAddress,
				rt: customerInformation.rt,
				rw: customerInformation.rw,
				postalId: customerInformation.address?.postalId,
				industrySectorId: customerInformation.industrySector?.id,
				companyGroupId: customerInformation.companyGroup
					? String(customerInformation.companyGroup?.groupId)
					: undefined,
				npwpNo: customerInformation.npwpNo,
				picName: customerInformation.picName,
				picPosition: customerInformation.picPosition?.id,
				additionalInformation: customerInformation.additionalInformation,
				deviation: customerInformation.deviation,
			},
			customerLegalDocuments:
				customerLegalDocuments
					?.filter((doc) => doc.file)
					?.map((doc) => {
						return {
							id: doc.id,
							file: doc.file && {
								id: doc.file.id,
								name: doc.file.name,
								path: doc.file.path,
							},
							documentNo: doc.documentNo,
							documentDate: doc.documentDate,
						};
					}) ?? [],
			customerReferralDocuments:
				customerReferralDocuments
					?.filter((doc) => doc.file)
					?.map((doc) => {
						return {
							id: doc.id!,
							file: doc.file && {
								id: doc.file.id,
								name: doc.file.name,
								path: doc.file.path,
							},
							documentNo: doc.documentNo,
							documentDate: doc.documentDate,
						};
					}) ?? [],
			customerBusinessDocuments:
				customerBusinessDocuments
					?.filter((doc) => doc.file)
					?.map((doc) => {
						return {
							id: doc.id,
							file: doc.file && {
								id: doc.file.id,
								name: doc.file.name,
								path: doc.file.path,
							},
							documentDate: doc.documentDate,
							documentNo: doc.documentNo,
							publisherId: doc.publisher?.id,
							validityPeriodId: doc.validityPeriod?.id,
						};
					}) ?? [],
			customerCompanyManagementInformation:
				customerCompanyManagementInformation?.map((doc) => {
					return {
						id: isESTAR ? undefined : doc?.id,
						customerId: customerDetail?.customerInformation.id,
						name: doc.name,
						citizenship: doc.citizenship?.id,
						nationalityId: doc.nationality?.id,
						identityCardType: doc.identityCardType?.id,
						identityNumber: doc.identityNumber,
						dateOfBirth: doc.dateOfBirth,
						position: doc.position?.id,
						address: doc.address,
						rt: doc.rt,
						rw: doc.rw,
						postalId: doc.postal?.postalId ?? 1,
						phoneNumber: doc.phoneNumber,
						status: doc.status?.id,
						identityFile: doc.identityFile && {
							id: doc.identityFile.id,
							name: doc.identityFile.name,
							path: doc.identityFile.path,
						},
						isShareholder: doc.isShareholder,
						shareholding: doc.shareholding,
					};
				}) ?? [],
			customerCompanyShareholderInformation:
				customerCompanyShareholderInformation
					?.filter((doc) => !doc.isManagement)
					?.map((doc) => {
						return {
							id: isESTAR ? undefined : doc?.id,
							customerId: customerDetail?.customerInformation?.id,
							name: doc.name,
							citizenship: doc.citizenship?.id,
							nationalityId: doc.nationality?.id,
							identityCardType: doc.identityCardType?.id,
							identityNumber: doc.identityNumber,
							address: doc.address,
							rt: doc.rt,
							rw: doc.rw,
							postalId: doc.postal?.postalId ?? 1,
							phoneNumber: doc.phoneNumber,
							shareholding: doc.shareholding,
							status: doc.status?.id ?? 1,
							identityFile: doc.identityFile && {
								id: doc.identityFile.id,
								name: doc.identityFile.name,
								path: doc.identityFile.path,
							},
							shareholderType: doc.shareholderType?.id,
						};
					}) ?? [],
		};

		const mutate =
			isEdit && customerDetail?.customerInformation.source !== 'Estar'
				? updateCustomerV2Mutation.mutateAsync
				: createCustomerV2Mutation.mutateAsync;

		try {
			const res = await mutate(payload);
			setShowNotes(false);

			await modal.success({
				title:
					isEdit && customerDetail?.customerInformation.source !== 'Estar' ? (
						'Update Data Anda Telah Berhasil'
					) : (
						<Fragment>
							{customerInformation.customerType === 1 ? (
								<>
									Penginputan Data Customer Anda Sedang dilakukan proses
									verifikasi
								</>
							) : (
								<>
									<small>Customer ID Anda</small>
									<div style={{ fontSize: 30 }}>{res.data}</div>
								</>
							)}
						</Fragment>
					),
				subTitle:
					customerInformation?.customerType === 1
						? null
						: 'Terdapat masa aktif new order yaitu 30 hari, jika lebih dari 30 hari maka status order in akan expired.',
			});

			if (isEdit) {
				await customerDetailQuery.refetch();
				navigate('CustomerListPage');
			} else {
				await customerDetailQuery.refetch();
				if (customerInformation.customerType === 1) {
					navigate('CustomerListPage');
				} else {
					navigate('FRMProspectPage', { customerId: res.data, index: 0 });
				}
			}
		} catch (error) {
			if (fetcher.isError(error)) {
				const message = await error.json();
				notification.error({
					duration: 20,
					message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
					description: (
						<div>
							{message?.metadata?.debugInfo?.messages &&
								message?.metadata?.debugInfo?.messages.length ? (
								<ul>
									{message?.metadata?.debugInfo?.messages.map((msg: string) => (
										<li>{msg}</li>
									))}
								</ul>
							) : (
								<pre>Opps, Something went wrong</pre>
							)}
						</div>
					),
				});
			}
		}
	};

	const isSubmitting = createCustomerV2Mutation.isPending;
	const customerDetail = customerDetailQuery.data?.data;
	const customerDocument = customerDocumentQuery.data?.data;

	useEffect(() => {
		if (customerDetail && isEdit && customerDetail.customerInformation?.id) {
			const customerType = form.getFieldValue([
				'customerInformation',
				'customerType',
			]);
			const initialValues = getInitialValue();
			form.resetFields();
			form.setFieldsValue(initialValues);
			if (initialValues.customerInformation.customerType !== null) {
				form.setFieldValue(
					['customerInformation', 'customerType'],
					customerType,
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [customerDetail?.customerInformation?.id, isEdit]);

	const getInitialValue = () => {
		const {
			customerInformation,
			customerCompanyManagementInformation,
			customerCompanyShareholderInformation,
		} = customerDetail ?? {};

		return {
			customerInformation: {
				customerType: customerInformation?.customerType?.id,
				businessEntity: customerInformation?.businessEntity,
				companyType: customerInformation?.companyType,
				customerName: customerInformation?.customerName,
				npwpNo: customerInformation?.npwpNo,
				companyEmail: customerInformation?.companyEmail,
				companyAddress: customerInformation?.companyAddress,
				companyGroup: customerInformation?.customerGroup && {
					groupId: customerInformation?.customerGroup.id,
					groupName: customerInformation?.customerGroup.value,
				},
				rt: customerInformation?.rt,
				rw: customerInformation?.rw,
				address: customerInformation?.address,
				industrySector: customerInformation?.industrySector,
				picName: customerInformation?.picName,
				picPosition: customerInformation?.picPosition,
				additionalInformation: customerInformation?.additionalInformation,
				deviation: customerInformation?.deviation,
			},
			customerCompanyManagementInformation:
				customerCompanyManagementInformation?.map((doc) => {
					return {
						id: doc.id ?? doc.identityNumber,
						tempId: doc.id ?? doc.identityNumber,
						customerId: customerInformation?.id,
						name: doc.name,
						citizenship: doc.citizenship,
						nationality: doc.nationality && {
							id: doc.nationality.id,
							name: doc.nationality.value,
						},
						identityCardType: doc.identityCardType,
						identityNumber: doc.identityNumber,
						dateOfBirth: doc.dateOfBirth || undefined,
						position: doc.position || undefined,
						address: doc.address,
						rt: doc.rt || undefined,
						rw: doc.rw || undefined,
						postal: doc.postal || undefined,
						phoneNumber: doc.phoneNumber,
						status: doc.status,
						identityFile: doc.identityFile,
						isShareholder: doc.isShareholder,
						shareholding: doc.shareholding,
					} as CustomerCompanyManagementInformationForm;
				}) ?? [],
			customerCompanyShareholderInformation: [
				...(customerCompanyManagementInformation
					? customerCompanyManagementInformation
						.filter((doc) => doc.isShareholder)
						.map((doc) => {
							return {
								id: doc.id ?? doc.identityNumber,
								tempId: doc.id ?? doc.identityNumber,
								customerId: customerInformation?.id,
								name: doc.name,
								citizenship: doc.citizenship,
								nationality: doc.nationality && {
									id: doc.nationality.id,
									name: doc.nationality.value,
								},
								identityCardType: doc.identityCardType,
								identityNumber: doc.identityNumber,
								address: doc.address || undefined,
								rt: doc.rt || undefined,
								rw: doc.rw || undefined,
								postal: doc.postal || undefined,
								phoneNumber: doc.phoneNumber,
								status: doc.status,
								identityFile: doc.identityFile,
								isManagement: true,
								shareholding: doc.shareholding,
								shareholderType: {
									id: 1,
									value: 'Perorangan',
								},
							} as CustomerCompanyShareholderInformationForm;
						})
					: []),
				...(customerCompanyShareholderInformation
					? customerCompanyShareholderInformation
						.filter((doc) => !doc.isManagement)
						.map((doc) => {
							return {
								id: doc.id ?? doc.identityNumber,
								customerId: customerInformation?.id,
								name: doc.name,
								citizenship: doc.citizenship,
								nationality: doc.nationality && {
									id: doc.nationality.id,
									name: doc.nationality.value,
								},
								identityCardType: doc.identityCardType,
								identityNumber: doc.identityNumber,
								address: doc.address || undefined,
								rt: doc.rt || undefined,
								rw: doc.rw || undefined,
								postal: doc.postal || undefined,
								phoneNumber: doc.phoneNumber,
								status: doc.status,
								identityFile: doc.identityFile,
								isManagement: doc.isManagement,
								shareholding: doc.shareholding,
								shareholderType: doc.shareholderType,
							} as CustomerCompanyShareholderInformationForm;
						})
					: []),
			],
		} as CreateCustomerForm;
	};

	if (customerDetailQuery.isError) return <GenericErrorMessage />;

	if (isEdit && customerDetailQuery.isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Provider
			value={{
				customerId: customerDetail?.customerInformation?.id ?? undefined,
				isEdit,
				externalTrigger,
				isSubmitting,
				totalShareholding,
				validationStatus,
				customerDetail,
				customerDocument,
				customerForm,
				businessForm,
				legalDocumentForm,
				managerForm,
				shareholderForm,
				referrallForm,
			}}
		>
			<FleetTitle>Add Customers</FleetTitle>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				key={customerDetail?.customerInformation?.id ?? 'new-customer'}
				form={form}
				initialValues={getInitialValue()}
				onFinish={handleSubmit}
				onFinishFailed={(err) => console.log(err)}
			>
				<Form.Item
					noStyle
					name={fieldName('customerBusinessDocuments')}
				/>
				<Form.Item
					noStyle
					name={fieldName('customerLegalDocuments')}
				/>
				<Form.Item
					noStyle
					name={fieldName('customerCompanyManagementInformation')}
				/>
				<Form.Item
					noStyle
					name={fieldName('customerCompanyShareholderInformation')}
				/>
				<Form.Item
					noStyle
					name={fieldName('customerReferralDocuments')}
				/>
				<Form.Item
					noStyle
					name={fieldName('notes')}
				/>
				<Form.Item
					name={fieldName(['customerInformation', 'customerType'])}
					label="Pilih Data Dokumen"
					labelCol={{ span: 'auto' }}
					colon={false}
				>
					<Radio.Group
						disabled={Boolean(value?.customerInformation?.customerType)}
					>
						<Radio
							value={1}
							style={{ marginRight: '10px' }}
						>
							Data JF
						</Radio>
						<Radio value={2}>Data NJF</Radio>
					</Radio.Group>
				</Form.Item>

				{typeof value?.customerInformation?.customerType === 'number' && (
					<Fragment>
						<Divider />
						<Tabs
							activeKey={activeTabKey}
							onChange={(key) => setActiveTabKey(key)}
							items={[
								{
									key: 'customer',
									label: 'Data Customer',
									children: (
										<FRMCustomerData
											onSubmit={() => setActiveTabKey('legal')}
										/>
									),
								},
								{
									key: 'legal',
									label: 'Data Legalitas',
									disabled: !customerDocument || !validationStatus.customer,
									children: (
										<FRMCustomerLegal
											onSubmit={() => setActiveTabKey('business')}
										/>
									),
								},
								{
									key: 'business',
									label: 'Data Perizinan Usaha',
									disabled:
										!customerDocument ||
										!validationStatus.legal ||
										!validationStatus.management ||
										!validationStatus.customer,
									children: (
										<FRMCustomerBusiness
											onSubmit={async () => {
												if (value.customerInformation?.customerType === 1) {
													setActiveTabKey('referral');
												} else if (
													isEdit &&
													customerDetail?.customerInformation.source !== 'Estar'
												) {
													setShowNotes(true);
												} else {
													form.submit();
												}
											}}
										/>
									),
								},
								...(value.customerInformation.customerType === 1
									? [
										{
											key: 'referral',
											label: 'Data Referral JF',
											disabled:
												!customerDocument ||
												!validationStatus.legal ||
												!validationStatus.management ||
												!validationStatus.customer,
											children: (
												<FRMCustomerReferral
													onSubmit={() => {
														if (
															isEdit &&
															customerDetail?.customerInformation.source !==
															'Estar'
														) {
															setShowNotes(true);
														} else {
															form.submit();
														}
													}}
												/>
											),
										},
									]
									: []),
							]}
						/>
						{customerDetail?.customerInformation?.status === 'RETURN' && (
							<Fragment>
								<Divider />
								<Alert
									message="History Return Customer"
									type="warning"
									className="custom-alert"
									showIcon
									style={{ marginBottom: 10 }}
								/>
								<Table
									size="small"
									scroll={{ x: 'max-content' }}
									bordered
									pagination={false}
									dataSource={
										customerDetail?.customerHistory
											?.filter((approval) => approval.event === 'Return')
											.map((approval, key) => {
												return {
													key,
													...approval,
												};
											}) ?? []
									}
									columns={[
										{
											title: 'Name',
											render: (_, record) => record.fullName,
										},
										{
											title: 'Date',
											align: 'center',
											render: (_, record) =>
												dayjs(record.date).format('DD/MM/YYYY HH:mm:ss'),
										},
										{
											title: 'Notes',
											width: '50%',
											render: (_, record) => record.notes,
										},
									]}
								/>
							</Fragment>
						)}
					</Fragment>
				)}
			</Form>

			<FleetModal
				open={showNotes}
				closeIcon={false}
				keyboard={false}
				maskClosable={false}
				width={800}
				okText="SAVE"
				okButtonProps={{
					disabled: !value?.notes,
					loading:
						createCustomerV2Mutation.isPending ||
						updateCustomerV2Mutation.isPending,
				}}
				onOk={async () => {
					await form.validateFields().then(() => {
						form.submit();
					});
				}}
				onCancel={() => setShowNotes(false)}
				afterClose={() => form.resetFields([fieldName('notes')])}
			>
				<Result
					title={
						<small>
							Silahkan tambahkan notes sebelum melakukan penyimpanan
						</small>
					}
				/>
				<FleetTitle>History Notes</FleetTitle>
				<Table
					scroll={{ x: 'max-content' }}
					bordered
					pagination={false}
					dataSource={
						customerDetail?.customerHistory?.map((approval, key) => {
							return {
								key,
								...approval,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.no,
						},
						{
							title: 'Name',
							render: (_, record) => record.fullName,
						},
						{
							title: 'Date',
							render: (_, record) =>
								dayjs(record.date).format('DD/MM/YYYY HH:mm:ss'),
						},
						{
							title: 'Notes',
							render: (_, record) => record.notes,
						},
					]}
					style={{ marginBottom: 30 }}
				/>
				<TextArea
					placeholder="Add note"
					rows={4}
					value={value?.notes}
					required
					onChange={(e) => {
						form.setFieldValue(fieldName('notes'), e.target.value);
					}}
				/>
			</FleetModal>

			<FRMCustomerBusinessForm {...businessForm} />
			<FRMCustomerLegalDocumentForm {...legalDocumentForm} />
			<FRMCustomerLegalManagerForm {...managerForm} />
			<FRMCustomerLegalShareholderForm {...shareholderForm} />
			<FRMCustomerReferralForm {...referrallForm} />
		</Provider>
	);
};

export default FRMCustomerPage;
